import { Empty } from "antd";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import { useAdvanceTable } from "hooks";
import usePrivileges from "hooks/access-control/usePrivileges";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import CreateOrUpdatePrivilegeModal from "./CreateOrUpdatePrivilegeModal";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import Guard from "../rbac/Guard";
import { PRIV } from "../rbac/privileges";

const customStyles = {
  backgroundColor: "#e8f7e3",
  color: "#2e7d32",
  padding: "3px 8px",
  borderRadius: "6px",
  fontSize: "0.65rem",
  border: "1px solid #d5ebd0",
  cursor: "pointer",
  fontWeight: 500,
};

export default function PrivilegeManagement() {
  const {
    privileges,
    addPrivilege,
    updatePrivilege,
    deletePrivilege,
    loading,
  } = usePrivileges();
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPriv, setSelectedPriv] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [privToDelete, setPrivToDelete] = useState(null);
  const [deletingPriv, setDeletingPriv] = useState(null);

  const handleView = (id) => {
    const privilege = privileges.find((p) => p.id === id);
    setSelectedPriv(privilege);
    setShowViewModal(true);
  };

  const handleUpdate = (id) => {
    const privilege = privileges.find((p) => p.id === id);
    setSelectedPriv(privilege);
    setShowUpdateModal(true);
  };

  const confirmDelete = (id) => {
    setPrivToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeletingPriv(privToDelete);
      await deletePrivilege(privToDelete);
    } finally {
      setDeletingPriv(null);
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "scope",
      header: "Scope",
      cell: ({ getValue }) => {
        return <span style={customStyles}>{getValue()}</span>;
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item onClick={() => handleView(item.id)}>
                Assign to role
              </Dropdown.Item>
              <Guard privilege={PRIV.UPDATE_PRIVILEGES}>
                <Dropdown.Item onClick={() => handleUpdate(item.id)}>
                  Edit
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_PRIVILEGES}>
                <Dropdown.Divider as="div" />
                <Dropdown.Item
                  onClick={() => confirmDelete(item.id)}
                  className="text-danger"
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: privileges,
    columns,
    sortable: true,
    pagination: true,
    perPage: 50,
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="key"
        title="Privileges"
        subtitle="Manage user privileges and control access across the platform."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <Guard privilege={PRIV.CREATE_PRIVILEGES}>
        <IconButton
          variant="falcon-default"
          size="sm"
          icon="plus"
          transform="shrink-3"
          onClick={() => setShowAddModal(true)}
          className="text-xs mb-2"
        >
          <span className="ms-1">Add privilege</span>
        </IconButton>
      </Guard>

      {privileges && privileges.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: "text-xxs mb-0 overflow-hidden",
            }}
          />
          <div className="mt-3">
            <AdvanceTableFooter
              rowInfo
              navButtons
              rowsPerPageSelection
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div>
          <Empty />
        </div>
      )}

      {/* add privilege modal */}
      <CreateOrUpdatePrivilegeModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSave={addPrivilege}
      />

      {/* update modal */}
      <CreateOrUpdatePrivilegeModal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        privilege={selectedPriv} // privilege to update
        onSave={updatePrivilege}
      />

      <ConfirmDeleteModal
        itemName="privilege"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        loading={deletingPriv === privToDelete}
      />
    </>
  );
}
